import type { Language } from "../../types/i18n";
import { appPagesAuthStrings } from "./appPagesAuth";
import { appPagesLandingStrings } from "./appPagesLanding";

/*
 * UI strings for one area of the app, merged into `translations` (see
 * ./index.ts) so components read them through `useI18nStore().t` like every
 * other key. English is the reference; the other languages must carry the
 * same keys, which the record type enforces. The landing page and the
 * registration pages keep their strings in ./appPagesLanding.ts and
 * ./appPagesAuth.ts; they are merged in below.
 */
const en = {
  demoStepTravelTitle: "Plan a trip",
  demoStepTravelDescription:
    "Create a trip to Thailand and get vaccination recommendations",
  demoStepHealthDataTitle: "Health data",
  demoStepHealthDataDescription: "Record medications and vaccinations",
  demoStepCheckinTitle: "Check-in",
  demoStepCheckinDescription: "Share emergency data for a cruise or flight",
  demoStepEmergencyTitle: "Emergency access",
  demoStepEmergencyDescription:
    "Share data with first responders or a doctor abroad",
  demoStepCardTitle: "Card & insurance",
  demoStepCardDescription:
    "Book an appointment and request medical repatriation",
  demoExit: "Exit demo",
  demoStart: "Start",
  demoStepOf: "Step {current} of {total}: {title}",
  demoFinish: "Finish",
  demoPageDescription:
    "Try all 5 demo scenarios, from trip planning to emergency care abroad",
  demoCountriesBadge: "140+ countries",
  demoMultilingual: "Multilingual",
  demoGdpr: "GDPR",
  demoTripTitle: "Demo trip: {from} → {to}",
  demoTripDescription:
    "Follow Terry Pazzo on his trip to {country} and see the complete HealthPass workflow, from trip planning to emergency care.",
  demoStartButton: "Start demo",
  demoScenariosHeading: "5 demo scenarios – start one directly:",
  demoScenarioLabel: "Scenario {n}",
  demoTestDataTitle: "Demo test data",
  demoDataUser: "User",
  demoDataLogin: "Login",
  demoDataHomeCountry: "Home country",
  demoDataDestination: "Destination",
  demoDataTravelPeriod: "Travel dates",
  demoDataMedication: "Demo medication",
  demoDataVaccination: "Demo vaccination",
  demoDataVaccinationValue: "Hepatitis A",
  demoDataOperator: "Operator (check-in)",
  demoCompleteTitle: "Demo complete!",
  demoCompleteDescription:
    "You have completed all 5 HealthPass demo scenarios.",
  demoCompletedScenarios: "Completed scenarios",
  demoRestart: "Repeat demo",
  demoToDashboard: "Go to dashboard",
  profErrFirstName: "First name must be between 2 and 50 characters.",
  profErrLastName: "Last name must be between 2 and 50 characters.",
  profErrPhone:
    "Phone number must be between 7 and 20 characters and may only contain digits, spaces, +, -, (, ).",
  profErrDateOfBirth:
    "Please select the month, day and year of your date of birth. The date must be between 1900 and today.",
  profErrBirthDay: "Please select a valid day (1-31).",
  profErrBirthMonth: "Please select a valid month.",
  profErrBirthYear: "Please select a valid year between 1900 and today.",
  profErrNationality: "Please select a nationality from the list.",
  profErrAddress: "Address can be at most 200 characters.",
  profErrCity: "City can be at most 100 characters.",
  profErrCountry: "Please select a country from the list.",
  profErrPostalCode: "ZIP code can be at most 20 characters.",
  profErrCheckField: 'Please check the field "{field}": {message}',
  profErrUnknownField: "Unknown field",
  profErrInvalidValue: "Invalid value",
  profUpdateFailed: "Update failed",
  profPhonePlaceholder: "(555) 123-4567",
  pwaInstallTitle: "Install HealthPass",
  pwaInstallDescription:
    "Install HealthPass for faster access and offline features",
  pwaInstallButton: "Install",
  pwaLater: "Later",
  pwaClose: "Close",
  pwaUpdateTitle: "Update available",
  pwaUpdateDescription: "A new version of HealthPass is available",
  pwaUpdateButton: "Update",
  errbTitle: "A technical error occurred",
  errbMessage: "Please try again or go back to the home page.",
  errbDeveloperDetails: "Developer details",
  errbRetry: "Try again",
  errbHome: "Go to home page",
  errbContactSupport: "If the problem persists, please contact support",
  settings2PasswordCurrentWrong: "The current password is not correct.",
  settings2PasswordNotAllowed: "The new password is not allowed.",
  settings2PasswordTooManyAttempts:
    "Too many attempts. Try again in 15 minutes.",
  settings2PasswordChangeFailed:
    "Your password could not be changed. Try again shortly.",
  settings2PasswordChanged:
    "Password changed. You have been signed out everywhere - sign in again with your new password.",
  settings2AccountSecurity: "Account Security",
  settings2CurrentPasswordPlaceholder: "Enter current password",
  settings2NewPasswordPlaceholder: "Enter new password",
  settings2RepeatPasswordPlaceholder: "Repeat new password",
  settings2PasswordsDoNotMatch: "The new passwords do not match.",
  settings2PortalLanguage: "Portal Language",
  settings2PortalLanguageHint:
    "Choose your preferred language for the user interface",
  settings2Notifications: "Notifications",
  settings2GeneralNotifications: "General Notifications",
  settings2EmailNotifications: "Email Notifications",
  settings2SmsNotifications: "SMS Notifications",
  settings2SmsNotificationsHint: "Urgent notifications via SMS",
  settings2PushNotifications: "Push Notifications",
  settings2PushUnsupported: "Not supported in this browser",
  settings2PushBlocked:
    "Blocked by browser – please enable in browser settings",
  settings2PushActive: "Active – browser notifications enabled",
  settings2PushEnableHint: "Enable real-time consent & health alerts",
  settings2PushUnavailableShort: "Unavailable",
  settings2PushBlockedShort: "Blocked",
  settings2MedicationReminders: "Medication reminders",
  settings2MedicationRemindersHint: "Reminders to take your medication",
  settings2TravelReminders: "Travel reminders",
  settings2TravelRemindersHint: "Vaccinations and documents for travel",
  settings2HealthCheckReminders: "Health check reminders",
  settings2HealthCheckRemindersHint: "Regular preventive checkups",
  settings2DataManagement: "Data Management",
  settings2ExportData: "Export data",
  settings2StartExport: "Start export",
  settings2MemberSince: "Member since",
  settings2Trips: "Trips",
  settings2PrivacySecurity: "Privacy & Security",
  settings2PrivacySecurityText:
    "Your data is stored encrypted and complies with GDPR requirements.",
  settings2StandardisedFormat: "Standardized format:",
  settings2ConsentPageTitle: "Privacy & Consents",
  settings2ConsentPageDescription:
    "Manage your data privacy preferences and consent settings",
  settings2GdprPageTitle: "GDPR Compliance & Data Export",
  settings2GdprPageDescription: "Exercise your data rights under the GDPR",
  settings2DeviceAndroid: "Android device",
  settings2DeviceWindows: "Windows PC",
  settings2DeviceThis: "This device",
  settings2PasskeyAddFailed:
    "That passkey could not be added. Please try again.",
  settings2PasskeyRemoveFailed: "That passkey could not be removed.",
  settings2EmailDeliveryFailed:
    "We could not send a code to your address, so nothing was changed.",
  settings2EmailNoAddress:
    "This account has no email address to send a code to.",
  settings2EmailSetupFailed: "We could not start the setup. Please try again.",
  settings2CodeInvalid: "That code is not valid.",
  settings2SettingChangeFailed: "We could not change that setting.",
  settings2CheckingSecurity: "Checking your sign-in security...",
  settings2SecurityLoadFailed:
    "We could not load your sign-in security settings.",
  settings2TwoFactorTitle: "Two-Factor Authentication",
  settings2FactorPasskeyDescription:
    "Signing in asks for your passkey after your password.",
  settings2FactorEmailToDescription:
    "Signing in asks for a code sent to {email}.",
  settings2FactorEmailDescription: "Signing in asks for an emailed code.",
  settings2FactorNoneDescription: "Signing in asks for your password only.",
  settings2BadgePasskey: "Passkey",
  settings2BadgeEmailCode: "Email code",
  settings2BadgeNotEnabled: "Not enabled",
  settings2Passkeys: "Passkeys",
  settings2WaitingForDevice: "Waiting for device...",
  settings2AddPasskey: "Add a passkey",
  settings2PasskeysUnsupported:
    "This browser does not support passkeys. An emailed code is the other option.",
  settings2ReauthMessage:
    "For your security, sign in again to change your sign-in methods.",
  settings2SignInAgain: "Sign in again",
  settings2NoPasskeys:
    "No passkeys yet. Adding one replaces the emailed code with your device's fingerprint, face or PIN.",
  settings2PasskeyAdded: "Added {date}",
  settings2PasskeyLastUsed: "last used {date}",
  settings2PasskeyNotUsed: "not used yet",
  settings2RemovePasskey: "Remove passkey {label}",
  settings2SecondPasskeyHint:
    "Adding a second passkey is worth it if you ever lose this device.",
  settings2EmailCodeSuperseded:
    "Your passkey is used instead. The emailed code would be the weaker of the two, so it is not offered while a passkey exists.",
  settings2EmailCodeSentTo: "A code is sent to {email} when you sign in.",
  settings2EmailCodeSentToYours:
    "A code is sent to your address when you sign in.",
  settings2TurningOff: "Turning off...",
  settings2TurnOff: "Turn off",
  settings2CodeSentEnter:
    "We sent a code to {email}. Enter it to finish turning this on.",
  settings2YourAddress: "your address",
  settings2EmailedCode: "Emailed code",
  settings2Checking: "Checking...",
  settings2Confirm: "Confirm",
  settings2EmailCodeOffer: "Ask for a code by email after your password.",
  settings2SendingCode: "Sending a code...",
  settings2TurnOn: "Turn on",
  miscToastLoginWelcome: "Login successful! Welcome back.",
  miscToastPasswordResetSent: "Password reset email sent.",
  miscToastDocumentUploaded: "Document uploaded and encrypted successfully.",
  miscToastDocumentsUploaded:
    "{count} documents uploaded and encrypted successfully.",
  miscToastUploadFailed: "Upload failed. Please try again.",
  miscToastUploadFailedWithReason: "Upload failed: {error}",
  miscToastDocumentDeleted: "Document deleted securely.",
  miscToastDocumentDownloaded: "Document decrypted and downloaded.",
  miscToastHealthRecordSaved: "Health data saved successfully.",
  miscToastVaccinationAdded: "Vaccination added successfully.",
  miscToastMedicationAdded: "Medication added successfully.",
  miscToastWellbeingSaved: "Wellbeing data saved to FHIR database.",
  miscToastWellbeingSaveFailed: "Failed to save wellbeing. Please try again.",
  miscToastSecurityAlert: "Security alert: {message}",
  miscToastTinaReady: "Tina AI is ready to help!",
  miscToastTinaUnavailable: "Tina AI is temporarily unavailable.",
  miscToastUnknownError: "An unknown error occurred.",
  unitsChangedToast: "{type} unit changed to {unit}",
  unitsUpdateFailed: "Couldn't update the unit preference",
  unitsResetToast: "Units now follow the app language again",
  unitsResetFailed: "Couldn't reset the unit preferences",
  unitsWeightTitle: "Weight",
  unitsHeightTitle: "Height",
  unitsTemperatureTitle: "Temperature",
  unitsBloodGlucoseTitle: "Blood glucose",
  unitsCholesterolTitle: "Cholesterol",
  unitsWeightDescription: "Choose your preferred unit for body weight",
  unitsHeightDescription: "Choose your preferred unit for height",
  unitsTemperatureDescription:
    "Choose your preferred unit for body temperature",
  unitsBloodGlucoseDescription: "Choose your preferred unit for blood glucose",
  unitsCholesterolDescription: "Choose your preferred unit for cholesterol",
  unitsKilograms: "Kilograms (kg)",
  unitsPounds: "Pounds (lb)",
  unitsStones: "Stones (st)",
  unitsCentimeters: "Centimeters (cm)",
  unitsInches: "Inches (in)",
  unitsFeetInches: "Feet & inches (ft/in)",
  unitsCelsius: "Celsius (°C)",
  unitsFahrenheit: "Fahrenheit (°F)",
  unitsMostCountries: "Most countries",
  unitsRegionUs: "USA",
  unitsRegionUk: "UK",
  unitsRegionUsUk: "USA, UK",
  unitsRegionUsJapan: "USA, Japan",
  unitsSelectOption: "Select {unit}",
  unitsLoginRequired: "Please log in to manage unit preferences",
  unitsPageTitle: "Unit Preferences",
  unitsPageDescription:
    "Customize how measurements are displayed throughout HealthPass. All data is stored in standard medical units and converted automatically for display.",
  unitsFollowLanguageTitle: "Units follow the app language",
  unitsFollowLanguageDescription:
    "In English, HealthPass shows US units (lb, ft/in, °F, mg/dL); in the other languages, metric units. You can choose individual units below.",
  unitsSaved: "Preferences saved!",
  unitsResetTitle: "Follow the app language",
  unitsResetDescription:
    "Discard your own choices and use the units of the app language again (English: US units, other languages: metric).",
  unitsResetButton: "Reset to defaults",
  unitsLoading: "Loading preferences...",
  unitsInchesShort: "inches",
  unitsFeetInchesShort: "feet & inches",
  unitsFeetPlaceholder: "Feet",
  unitsInchesPlaceholder: "Inches",
  unitsConverting: "Converting...",
  unitsPreview: "Preview:",
  unitsYourPreferredUnit: "(your preferred unit)",
};

type CoreStrings = typeof en;

const core: Record<Language, CoreStrings> = {
  en,
  de: {
    demoStepTravelTitle: "Reise planen",
    demoStepTravelDescription:
      "Thailand-Reise anlegen & Impfempfehlungen erhalten",
    demoStepHealthDataTitle: "Gesundheitsdaten",
    demoStepHealthDataDescription: "Medikamente & Impfungen erfassen",
    demoStepCheckinTitle: "Check-in",
    demoStepCheckinDescription: "Notfalldaten auf Kreuzfahrt / Flug übergeben",
    demoStepEmergencyTitle: "Notfall-Zugang",
    demoStepEmergencyDescription:
      "Daten an Ersthelfer / Arzt im Ausland übergeben",
    demoStepCardTitle: "Karte & Versicherung",
    demoStepCardDescription: "Termin buchen & Reiserücktransport anfordern",
    demoExit: "Demo beenden",
    demoStart: "Start",
    demoStepOf: "Schritt {current} / {total}: {title}",
    demoFinish: "Abschluss",
    demoPageDescription:
      "Erleben Sie alle 5 Demo-Szenarien – von der Reiseplanung bis zur Notfallversorgung im Ausland",
    demoCountriesBadge: "140+ Länder",
    demoMultilingual: "Mehrsprachig",
    demoGdpr: "DSGVO",
    demoTripTitle: "Demo-Reise: {from} → {to}",
    demoTripDescription:
      "Folgen Sie Terry Pazzo auf seiner Reise nach {country} und erleben Sie den vollständigen HealthPass-Workflow – von der Reiseplanung bis zur Notfallversorgung.",
    demoStartButton: "Demo starten",
    demoScenariosHeading: "5 Demo-Szenarien – direkt starten:",
    demoScenarioLabel: "Szenario {n}",
    demoTestDataTitle: "Demo-Testdaten",
    demoDataUser: "Nutzer",
    demoDataLogin: "Login",
    demoDataHomeCountry: "Heimatland",
    demoDataDestination: "Zielland",
    demoDataTravelPeriod: "Reisezeitraum",
    demoDataMedication: "Demo-Medikament",
    demoDataVaccination: "Demo-Impfung",
    demoDataVaccinationValue: "Hepatitis A",
    demoDataOperator: "Operator (Check-in)",
    demoCompleteTitle: "Demo abgeschlossen!",
    demoCompleteDescription:
      "Sie haben alle 5 HealthPass-Demo-Szenarien erfolgreich durchlaufen.",
    demoCompletedScenarios: "Absolvierte Szenarien",
    demoRestart: "Demo wiederholen",
    demoToDashboard: "Zum Dashboard",
    profErrFirstName: "Der Vorname muss zwischen 2 und 50 Zeichen lang sein.",
    profErrLastName: "Der Nachname muss zwischen 2 und 50 Zeichen lang sein.",
    profErrPhone:
      "Die Telefonnummer muss zwischen 7 und 20 Zeichen lang sein und darf nur Ziffern, Leerzeichen, +, -, (, ) enthalten.",
    profErrDateOfBirth:
      "Bitte wählen Sie Tag, Monat und Jahr Ihres Geburtsdatums. Das Datum muss zwischen 1900 und heute liegen.",
    profErrBirthDay: "Bitte wählen Sie einen gültigen Tag (1–31).",
    profErrBirthMonth: "Bitte wählen Sie einen gültigen Monat.",
    profErrBirthYear:
      "Bitte wählen Sie ein gültiges Jahr zwischen 1900 und heute.",
    profErrNationality:
      "Bitte wählen Sie eine Staatsangehörigkeit aus der Liste.",
    profErrAddress: "Die Adresse darf höchstens 200 Zeichen lang sein.",
    profErrCity: "Der Ortsname darf höchstens 100 Zeichen lang sein.",
    profErrCountry: "Bitte wählen Sie ein Land aus der Liste.",
    profErrPostalCode: "Die Postleitzahl darf höchstens 20 Zeichen lang sein.",
    profErrCheckField: "Bitte prüfen Sie das Feld „{field}“: {message}",
    profErrUnknownField: "Unbekanntes Feld",
    profErrInvalidValue: "Ungültiger Wert",
    profUpdateFailed: "Aktualisierung fehlgeschlagen",
    profPhonePlaceholder: "+49 30 1234567",
    pwaInstallTitle: "HealthPass installieren",
    pwaInstallDescription:
      "Installieren Sie HealthPass für schnelleren Zugriff und Offline-Funktionen",
    pwaInstallButton: "Installieren",
    pwaLater: "Später",
    pwaClose: "Schließen",
    pwaUpdateTitle: "Update verfügbar",
    pwaUpdateDescription: "Eine neue Version von HealthPass ist verfügbar",
    pwaUpdateButton: "Aktualisieren",
    errbTitle: "Ein technischer Fehler ist aufgetreten",
    errbMessage:
      "Bitte versuchen Sie es erneut oder kehren Sie zur Startseite zurück.",
    errbDeveloperDetails: "Entwickler-Details",
    errbRetry: "Erneut versuchen",
    errbHome: "Zur Startseite",
    errbContactSupport:
      "Bei anhaltenden Problemen kontaktieren Sie bitte den Support",
    settings2PasswordCurrentWrong: "Das aktuelle Passwort ist nicht korrekt.",
    settings2PasswordNotAllowed: "Das neue Passwort ist nicht zulässig.",
    settings2PasswordTooManyAttempts:
      "Zu viele Versuche. Bitte versuchen Sie es in 15 Minuten erneut.",
    settings2PasswordChangeFailed:
      "Ihr Passwort konnte nicht geändert werden. Bitte versuchen Sie es gleich noch einmal.",
    settings2PasswordChanged:
      "Passwort geändert. Sie wurden überall abgemeldet – melden Sie sich mit Ihrem neuen Passwort wieder an.",
    settings2AccountSecurity: "Kontosicherheit",
    settings2CurrentPasswordPlaceholder: "Aktuelles Passwort eingeben",
    settings2NewPasswordPlaceholder: "Neues Passwort eingeben",
    settings2RepeatPasswordPlaceholder: "Neues Passwort wiederholen",
    settings2PasswordsDoNotMatch: "Die neuen Passwörter stimmen nicht überein.",
    settings2PortalLanguage: "Sprache des Portals",
    settings2PortalLanguageHint:
      "Wählen Sie Ihre bevorzugte Sprache für die Benutzeroberfläche",
    settings2Notifications: "Benachrichtigungen",
    settings2GeneralNotifications: "Allgemeine Benachrichtigungen",
    settings2EmailNotifications: "E-Mail-Benachrichtigungen",
    settings2SmsNotifications: "SMS-Benachrichtigungen",
    settings2SmsNotificationsHint: "Dringende Benachrichtigungen per SMS",
    settings2PushNotifications: "Push-Benachrichtigungen",
    settings2PushUnsupported: "In diesem Browser nicht unterstützt",
    settings2PushBlocked:
      "Vom Browser blockiert – bitte in den Browsereinstellungen aktivieren",
    settings2PushActive: "Aktiv – Browser-Benachrichtigungen eingeschaltet",
    settings2PushEnableHint:
      "Echtzeit-Hinweise zu Einwilligungen und Gesundheit aktivieren",
    settings2PushUnavailableShort: "Nicht verfügbar",
    settings2PushBlockedShort: "Blockiert",
    settings2MedicationReminders: "Medikamentenerinnerungen",
    settings2MedicationRemindersHint:
      "Erinnerungen an die Einnahme Ihrer Medikamente",
    settings2TravelReminders: "Reiseerinnerungen",
    settings2TravelRemindersHint: "Impfungen und Dokumente für Reisen",
    settings2HealthCheckReminders: "Erinnerungen an Vorsorgeuntersuchungen",
    settings2HealthCheckRemindersHint: "Regelmäßige Vorsorgeuntersuchungen",
    settings2DataManagement: "Datenverwaltung",
    settings2ExportData: "Daten exportieren",
    settings2StartExport: "Export starten",
    settings2MemberSince: "Mitglied seit",
    settings2Trips: "Reisen",
    settings2PrivacySecurity: "Datenschutz & Sicherheit",
    settings2PrivacySecurityText:
      "Ihre Daten werden verschlüsselt gespeichert und erfüllen die Anforderungen der DSGVO.",
    settings2StandardisedFormat: "Standardisiertes Format:",
    settings2ConsentPageTitle: "Datenschutz & Einwilligungen",
    settings2ConsentPageDescription:
      "Verwalten Sie Ihre Datenschutzeinstellungen und Einwilligungen",
    settings2GdprPageTitle: "DSGVO & Datenexport",
    settings2GdprPageDescription: "Nehmen Sie Ihre Rechte nach der DSGVO wahr",
    settings2DeviceAndroid: "Android-Gerät",
    settings2DeviceWindows: "Windows-PC",
    settings2DeviceThis: "Dieses Gerät",
    settings2PasskeyAddFailed:
      "Der Passkey konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.",
    settings2PasskeyRemoveFailed: "Der Passkey konnte nicht entfernt werden.",
    settings2EmailDeliveryFailed:
      "Wir konnten keinen Code an Ihre Adresse senden, daher wurde nichts geändert.",
    settings2EmailNoAddress:
      "Für dieses Konto ist keine E-Mail-Adresse hinterlegt, an die ein Code gesendet werden kann.",
    settings2EmailSetupFailed:
      "Die Einrichtung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
    settings2CodeInvalid: "Dieser Code ist ungültig.",
    settings2SettingChangeFailed:
      "Diese Einstellung konnte nicht geändert werden.",
    settings2CheckingSecurity: "Ihre Anmeldesicherheit wird geprüft...",
    settings2SecurityLoadFailed:
      "Ihre Einstellungen zur Anmeldesicherheit konnten nicht geladen werden.",
    settings2TwoFactorTitle: "Zwei-Faktor-Authentifizierung",
    settings2FactorPasskeyDescription:
      "Bei der Anmeldung wird nach dem Passwort Ihr Passkey abgefragt.",
    settings2FactorEmailToDescription:
      "Bei der Anmeldung wird ein Code abgefragt, der an {email} gesendet wird.",
    settings2FactorEmailDescription:
      "Bei der Anmeldung wird ein per E-Mail gesendeter Code abgefragt.",
    settings2FactorNoneDescription:
      "Bei der Anmeldung wird nur Ihr Passwort abgefragt.",
    settings2BadgePasskey: "Passkey",
    settings2BadgeEmailCode: "E-Mail-Code",
    settings2BadgeNotEnabled: "Nicht aktiviert",
    settings2Passkeys: "Passkeys",
    settings2WaitingForDevice: "Warte auf Gerät...",
    settings2AddPasskey: "Passkey hinzufügen",
    settings2PasskeysUnsupported:
      "Dieser Browser unterstützt keine Passkeys. Die Alternative ist ein Code per E-Mail.",
    settings2ReauthMessage:
      "Melden Sie sich zu Ihrer Sicherheit erneut an, um Ihre Anmeldemethoden zu ändern.",
    settings2SignInAgain: "Erneut anmelden",
    settings2NoPasskeys:
      "Noch keine Passkeys. Ein Passkey ersetzt den E-Mail-Code durch Fingerabdruck, Gesichtserkennung oder PIN Ihres Geräts.",
    settings2PasskeyAdded: "Hinzugefügt am {date}",
    settings2PasskeyLastUsed: "zuletzt verwendet am {date}",
    settings2PasskeyNotUsed: "noch nicht verwendet",
    settings2RemovePasskey: "Passkey {label} entfernen",
    settings2SecondPasskeyHint:
      "Ein zweiter Passkey lohnt sich, falls Sie dieses Gerät einmal verlieren.",
    settings2EmailCodeSuperseded:
      "Stattdessen wird Ihr Passkey verwendet. Der E-Mail-Code wäre die schwächere der beiden Methoden und wird daher nicht angeboten, solange ein Passkey existiert.",
    settings2EmailCodeSentTo:
      "Bei der Anmeldung wird ein Code an {email} gesendet.",
    settings2EmailCodeSentToYours:
      "Bei der Anmeldung wird ein Code an Ihre Adresse gesendet.",
    settings2TurningOff: "Wird ausgeschaltet...",
    settings2TurnOff: "Ausschalten",
    settings2CodeSentEnter:
      "Wir haben einen Code an {email} gesendet. Geben Sie ihn ein, um die Einrichtung abzuschließen.",
    settings2YourAddress: "Ihre Adresse",
    settings2EmailedCode: "Code aus der E-Mail",
    settings2Checking: "Wird geprüft...",
    settings2Confirm: "Bestätigen",
    settings2EmailCodeOffer:
      "Nach dem Passwort einen Code per E-Mail abfragen.",
    settings2SendingCode: "Code wird gesendet...",
    settings2TurnOn: "Einschalten",
    miscToastLoginWelcome: "Anmeldung erfolgreich! Willkommen zurück.",
    miscToastPasswordResetSent:
      "E-Mail zum Zurücksetzen des Passworts wurde gesendet.",
    miscToastDocumentUploaded:
      "Dokument erfolgreich hochgeladen und verschlüsselt.",
    miscToastDocumentsUploaded:
      "{count} Dokumente erfolgreich hochgeladen und verschlüsselt.",
    miscToastUploadFailed:
      "Upload fehlgeschlagen. Bitte versuchen Sie es erneut.",
    miscToastUploadFailedWithReason: "Upload fehlgeschlagen: {error}",
    miscToastDocumentDeleted: "Dokument wurde sicher gelöscht.",
    miscToastDocumentDownloaded:
      "Dokument wurde entschlüsselt und heruntergeladen.",
    miscToastHealthRecordSaved:
      "Gesundheitsdaten wurden erfolgreich gespeichert.",
    miscToastVaccinationAdded: "Impfung wurde erfolgreich hinzugefügt.",
    miscToastMedicationAdded: "Medikament wurde erfolgreich hinzugefügt.",
    miscToastWellbeingSaved:
      "Wohlbefinden wurde in FHIR-Datenbank gespeichert.",
    miscToastWellbeingSaveFailed:
      "Fehler beim Speichern des Wohlbefindens. Bitte versuchen Sie es erneut.",
    miscToastSecurityAlert: "Sicherheitshinweis: {message}",
    miscToastTinaReady: "Tina AI ist bereit zu helfen!",
    miscToastTinaUnavailable: "Tina AI ist vorübergehend nicht verfügbar.",
    miscToastUnknownError: "Ein unbekannter Fehler ist aufgetreten.",
    unitsChangedToast: "Einheit für {type} auf {unit} geändert",
    unitsUpdateFailed: "Die Einheit konnte nicht geändert werden",
    unitsResetToast: "Die Einheiten richten sich wieder nach der App-Sprache",
    unitsResetFailed: "Die Einheiten konnten nicht zurückgesetzt werden",
    unitsWeightTitle: "Gewicht",
    unitsHeightTitle: "Größe",
    unitsTemperatureTitle: "Temperatur",
    unitsBloodGlucoseTitle: "Blutzucker",
    unitsCholesterolTitle: "Cholesterin",
    unitsWeightDescription:
      "Wählen Sie Ihre bevorzugte Einheit für das Körpergewicht",
    unitsHeightDescription:
      "Wählen Sie Ihre bevorzugte Einheit für die Körpergröße",
    unitsTemperatureDescription:
      "Wählen Sie Ihre bevorzugte Einheit für die Körpertemperatur",
    unitsBloodGlucoseDescription:
      "Wählen Sie Ihre bevorzugte Einheit für den Blutzucker",
    unitsCholesterolDescription:
      "Wählen Sie Ihre bevorzugte Einheit für das Cholesterin",
    unitsKilograms: "Kilogramm (kg)",
    unitsPounds: "Pfund (lb)",
    unitsStones: "Stone (st)",
    unitsCentimeters: "Zentimeter (cm)",
    unitsInches: "Zoll (in)",
    unitsFeetInches: "Fuß & Zoll (ft/in)",
    unitsCelsius: "Celsius (°C)",
    unitsFahrenheit: "Fahrenheit (°F)",
    unitsMostCountries: "Die meisten Länder",
    unitsRegionUs: "USA",
    unitsRegionUk: "Vereinigtes Königreich",
    unitsRegionUsUk: "USA, Vereinigtes Königreich",
    unitsRegionUsJapan: "USA, Japan",
    unitsSelectOption: "{unit} auswählen",
    unitsLoginRequired:
      "Bitte melden Sie sich an, um Ihre Einheiten zu verwalten",
    unitsPageTitle: "Maßeinheiten",
    unitsPageDescription:
      "Legen Sie fest, wie Messwerte in HealthPass angezeigt werden. Alle Daten werden in medizinischen Standardeinheiten gespeichert und für die Anzeige automatisch umgerechnet.",
    unitsFollowLanguageTitle: "Die Einheiten richten sich nach der App-Sprache",
    unitsFollowLanguageDescription:
      "Auf Englisch zeigt HealthPass US-Einheiten (lb, ft/in, °F, mg/dL), in den anderen Sprachen metrische Einheiten. Unten können Sie einzelne Einheiten selbst wählen.",
    unitsSaved: "Einstellungen gespeichert!",
    unitsResetTitle: "Nach App-Sprache richten",
    unitsResetDescription:
      "Eigene Auswahl verwerfen und wieder die Einheiten der App-Sprache verwenden (Englisch: US-Einheiten, andere Sprachen: metrisch).",
    unitsResetButton: "Auf Standard zurücksetzen",
    unitsLoading: "Einstellungen werden geladen...",
    unitsInchesShort: "Zoll",
    unitsFeetInchesShort: "Fuß & Zoll",
    unitsFeetPlaceholder: "Fuß",
    unitsInchesPlaceholder: "Zoll",
    unitsConverting: "Wird umgerechnet...",
    unitsPreview: "Vorschau:",
    unitsYourPreferredUnit: "(Ihre bevorzugte Einheit)",
  },
  fr: {
    demoStepTravelTitle: "Planifier un voyage",
    demoStepTravelDescription:
      "Créer un voyage en Thaïlande et obtenir des recommandations de vaccination",
    demoStepHealthDataTitle: "Données de santé",
    demoStepHealthDataDescription: "Saisir les médicaments et les vaccinations",
    demoStepCheckinTitle: "Enregistrement",
    demoStepCheckinDescription:
      "Transmettre les données d'urgence pour une croisière ou un vol",
    demoStepEmergencyTitle: "Accès d'urgence",
    demoStepEmergencyDescription:
      "Transmettre les données aux secouristes ou à un médecin à l'étranger",
    demoStepCardTitle: "Carte et assurance",
    demoStepCardDescription:
      "Prendre rendez-vous et demander un rapatriement sanitaire",
    demoExit: "Quitter la démo",
    demoStart: "Début",
    demoStepOf: "Étape {current} sur {total} : {title}",
    demoFinish: "Terminer",
    demoPageDescription:
      "Découvrez les 5 scénarios de démo, de la planification du voyage aux soins d'urgence à l'étranger",
    demoCountriesBadge: "Plus de 140 pays",
    demoMultilingual: "Multilingue",
    demoGdpr: "RGPD",
    demoTripTitle: "Voyage de démo : {from} → {to}",
    demoTripDescription:
      "Suivez Terry Pazzo lors de son voyage en {country} et découvrez le parcours complet de HealthPass, de la planification du voyage aux soins d'urgence.",
    demoStartButton: "Lancer la démo",
    demoScenariosHeading: "5 scénarios de démo – lancez-en un directement :",
    demoScenarioLabel: "Scénario {n}",
    demoTestDataTitle: "Données de test de la démo",
    demoDataUser: "Utilisateur",
    demoDataLogin: "Identifiant",
    demoDataHomeCountry: "Pays d'origine",
    demoDataDestination: "Destination",
    demoDataTravelPeriod: "Dates du voyage",
    demoDataMedication: "Médicament de démo",
    demoDataVaccination: "Vaccination de démo",
    demoDataVaccinationValue: "Hépatite A",
    demoDataOperator: "Opérateur (enregistrement)",
    demoCompleteTitle: "Démo terminée !",
    demoCompleteDescription:
      "Vous avez terminé les 5 scénarios de démo HealthPass.",
    demoCompletedScenarios: "Scénarios terminés",
    demoRestart: "Recommencer la démo",
    demoToDashboard: "Aller au tableau de bord",
    profErrFirstName: "Le prénom doit comporter entre 2 et 50 caractères.",
    profErrLastName: "Le nom doit comporter entre 2 et 50 caractères.",
    profErrPhone:
      "Le numéro de téléphone doit comporter entre 7 et 20 caractères et ne peut contenir que des chiffres, des espaces, +, -, (, ).",
    profErrDateOfBirth:
      "Veuillez sélectionner le jour, le mois et l'année de votre date de naissance. La date doit être comprise entre 1900 et aujourd'hui.",
    profErrBirthDay: "Veuillez sélectionner un jour valide (1-31).",
    profErrBirthMonth: "Veuillez sélectionner un mois valide.",
    profErrBirthYear:
      "Veuillez sélectionner une année valide entre 1900 et aujourd'hui.",
    profErrNationality: "Veuillez sélectionner une nationalité dans la liste.",
    profErrAddress: "L'adresse ne peut pas dépasser 200 caractères.",
    profErrCity: "Le nom de la ville ne peut pas dépasser 100 caractères.",
    profErrCountry: "Veuillez sélectionner un pays dans la liste.",
    profErrPostalCode: "Le code postal ne peut pas dépasser 20 caractères.",
    profErrCheckField: "Veuillez vérifier le champ « {field} » : {message}",
    profErrUnknownField: "Champ inconnu",
    profErrInvalidValue: "Valeur non valide",
    profUpdateFailed: "Échec de la mise à jour",
    profPhonePlaceholder: "+33 1 23 45 67 89",
    pwaInstallTitle: "Installer HealthPass",
    pwaInstallDescription:
      "Installez HealthPass pour un accès plus rapide et les fonctions hors ligne",
    pwaInstallButton: "Installer",
    pwaLater: "Plus tard",
    pwaClose: "Fermer",
    pwaUpdateTitle: "Mise à jour disponible",
    pwaUpdateDescription: "Une nouvelle version de HealthPass est disponible",
    pwaUpdateButton: "Mettre à jour",
    errbTitle: "Une erreur technique s'est produite",
    errbMessage: "Veuillez réessayer ou revenir à la page d'accueil.",
    errbDeveloperDetails: "Détails pour les développeurs",
    errbRetry: "Réessayer",
    errbHome: "Aller à l'accueil",
    errbContactSupport:
      "Si le problème persiste, veuillez contacter le support",
    settings2PasswordCurrentWrong: "Le mot de passe actuel est incorrect.",
    settings2PasswordNotAllowed: "Le nouveau mot de passe n'est pas autorisé.",
    settings2PasswordTooManyAttempts:
      "Trop de tentatives. Réessayez dans 15 minutes.",
    settings2PasswordChangeFailed:
      "Votre mot de passe n'a pas pu être modifié. Réessayez dans un instant.",
    settings2PasswordChanged:
      "Mot de passe modifié. Vous avez été déconnecté partout : reconnectez-vous avec votre nouveau mot de passe.",
    settings2AccountSecurity: "Sécurité du compte",
    settings2CurrentPasswordPlaceholder: "Saisissez le mot de passe actuel",
    settings2NewPasswordPlaceholder: "Saisissez le nouveau mot de passe",
    settings2RepeatPasswordPlaceholder: "Répétez le nouveau mot de passe",
    settings2PasswordsDoNotMatch:
      "Les nouveaux mots de passe ne correspondent pas.",
    settings2PortalLanguage: "Langue du portail",
    settings2PortalLanguageHint:
      "Choisissez votre langue préférée pour l'interface",
    settings2Notifications: "Notifications",
    settings2GeneralNotifications: "Notifications générales",
    settings2EmailNotifications: "Notifications par e-mail",
    settings2SmsNotifications: "Notifications par SMS",
    settings2SmsNotificationsHint: "Notifications urgentes par SMS",
    settings2PushNotifications: "Notifications push",
    settings2PushUnsupported: "Non pris en charge par ce navigateur",
    settings2PushBlocked:
      "Bloqué par le navigateur – activez-les dans les paramètres du navigateur",
    settings2PushActive: "Actives – notifications du navigateur activées",
    settings2PushEnableHint:
      "Activer les alertes de consentement et de santé en temps réel",
    settings2PushUnavailableShort: "Indisponible",
    settings2PushBlockedShort: "Bloqué",
    settings2MedicationReminders: "Rappels de médicaments",
    settings2MedicationRemindersHint: "Rappels de prise de vos médicaments",
    settings2TravelReminders: "Rappels de voyage",
    settings2TravelRemindersHint: "Vaccinations et documents pour voyager",
    settings2HealthCheckReminders: "Rappels de bilans de santé",
    settings2HealthCheckRemindersHint: "Bilans de prévention réguliers",
    settings2DataManagement: "Gestion des données",
    settings2ExportData: "Exporter les données",
    settings2StartExport: "Lancer l'export",
    settings2MemberSince: "Membre depuis",
    settings2Trips: "Voyages",
    settings2PrivacySecurity: "Confidentialité et sécurité",
    settings2PrivacySecurityText:
      "Vos données sont stockées chiffrées et conformes aux exigences du RGPD.",
    settings2StandardisedFormat: "Format standardisé :",
    settings2ConsentPageTitle: "Confidentialité et consentements",
    settings2ConsentPageDescription:
      "Gérez vos préférences de confidentialité et vos consentements",
    settings2GdprPageTitle: "Conformité RGPD et export des données",
    settings2GdprPageDescription:
      "Exercez vos droits sur vos données au titre du RGPD",
    settings2DeviceAndroid: "Appareil Android",
    settings2DeviceWindows: "PC Windows",
    settings2DeviceThis: "Cet appareil",
    settings2PasskeyAddFailed:
      "Cette clé d'accès n'a pas pu être ajoutée. Veuillez réessayer.",
    settings2PasskeyRemoveFailed:
      "Cette clé d'accès n'a pas pu être supprimée.",
    settings2EmailDeliveryFailed:
      "Nous n'avons pas pu envoyer de code à votre adresse, rien n'a donc été modifié.",
    settings2EmailNoAddress:
      "Ce compte n'a pas d'adresse e-mail à laquelle envoyer un code.",
    settings2EmailSetupFailed:
      "Impossible de lancer la configuration. Veuillez réessayer.",
    settings2CodeInvalid: "Ce code n'est pas valide.",
    settings2SettingChangeFailed: "Impossible de modifier ce paramètre.",
    settings2CheckingSecurity: "Vérification de la sécurité de connexion...",
    settings2SecurityLoadFailed:
      "Impossible de charger vos paramètres de sécurité de connexion.",
    settings2TwoFactorTitle: "Authentification à deux facteurs",
    settings2FactorPasskeyDescription:
      "La connexion demande votre clé d'accès après votre mot de passe.",
    settings2FactorEmailToDescription:
      "La connexion demande un code envoyé à {email}.",
    settings2FactorEmailDescription:
      "La connexion demande un code reçu par e-mail.",
    settings2FactorNoneDescription:
      "La connexion ne demande que votre mot de passe.",
    settings2BadgePasskey: "Clé d'accès",
    settings2BadgeEmailCode: "Code par e-mail",
    settings2BadgeNotEnabled: "Non activée",
    settings2Passkeys: "Clés d'accès",
    settings2WaitingForDevice: "En attente de l'appareil...",
    settings2AddPasskey: "Ajouter une clé d'accès",
    settings2PasskeysUnsupported:
      "Ce navigateur ne prend pas en charge les clés d'accès. L'autre option est un code par e-mail.",
    settings2ReauthMessage:
      "Pour votre sécurité, reconnectez-vous pour modifier vos méthodes de connexion.",
    settings2SignInAgain: "Se reconnecter",
    settings2NoPasskeys:
      "Aucune clé d'accès pour l'instant. En ajouter une remplace le code par e-mail par l'empreinte digitale, le visage ou le code PIN de votre appareil.",
    settings2PasskeyAdded: "Ajoutée le {date}",
    settings2PasskeyLastUsed: "dernière utilisation le {date}",
    settings2PasskeyNotUsed: "pas encore utilisée",
    settings2RemovePasskey: "Supprimer la clé d'accès {label}",
    settings2SecondPasskeyHint:
      "Ajouter une deuxième clé d'accès est utile si vous perdez un jour cet appareil.",
    settings2EmailCodeSuperseded:
      "Votre clé d'accès est utilisée à la place. Le code par e-mail serait la méthode la plus faible des deux ; il n'est donc pas proposé tant qu'une clé d'accès existe.",
    settings2EmailCodeSentTo:
      "Un code est envoyé à {email} lorsque vous vous connectez.",
    settings2EmailCodeSentToYours:
      "Un code est envoyé à votre adresse lorsque vous vous connectez.",
    settings2TurningOff: "Désactivation...",
    settings2TurnOff: "Désactiver",
    settings2CodeSentEnter:
      "Nous avons envoyé un code à {email}. Saisissez-le pour terminer l'activation.",
    settings2YourAddress: "votre adresse",
    settings2EmailedCode: "Code reçu par e-mail",
    settings2Checking: "Vérification...",
    settings2Confirm: "Confirmer",
    settings2EmailCodeOffer:
      "Demander un code par e-mail après votre mot de passe.",
    settings2SendingCode: "Envoi d'un code...",
    settings2TurnOn: "Activer",
    miscToastLoginWelcome: "Connexion réussie ! Bon retour parmi nous.",
    miscToastPasswordResetSent:
      "L'e-mail de réinitialisation du mot de passe a été envoyé.",
    miscToastDocumentUploaded: "Document téléversé et chiffré avec succès.",
    miscToastDocumentsUploaded:
      "{count} documents téléversés et chiffrés avec succès.",
    miscToastUploadFailed: "Échec du téléversement. Veuillez réessayer.",
    miscToastUploadFailedWithReason: "Échec du téléversement : {error}",
    miscToastDocumentDeleted: "Document supprimé de manière sécurisée.",
    miscToastDocumentDownloaded: "Document déchiffré et téléchargé.",
    miscToastHealthRecordSaved: "Données de santé enregistrées avec succès.",
    miscToastVaccinationAdded: "Vaccination ajoutée avec succès.",
    miscToastMedicationAdded: "Médicament ajouté avec succès.",
    miscToastWellbeingSaved:
      "Données de bien-être enregistrées dans la base FHIR.",
    miscToastWellbeingSaveFailed:
      "Échec de l'enregistrement du bien-être. Veuillez réessayer.",
    miscToastSecurityAlert: "Alerte de sécurité : {message}",
    miscToastTinaReady: "Tina AI est prête à vous aider !",
    miscToastTinaUnavailable: "Tina AI est temporairement indisponible.",
    miscToastUnknownError: "Une erreur inconnue s'est produite.",
    unitsChangedToast: "Unité « {type} » changée en {unit}",
    unitsUpdateFailed: "Impossible de mettre à jour la préférence d'unité",
    unitsResetToast: "Les unités suivent de nouveau la langue de l'application",
    unitsResetFailed: "Impossible de réinitialiser les préférences d'unités",
    unitsWeightTitle: "Poids",
    unitsHeightTitle: "Taille",
    unitsTemperatureTitle: "Température",
    unitsBloodGlucoseTitle: "Glycémie",
    unitsCholesterolTitle: "Cholestérol",
    unitsWeightDescription:
      "Choisissez votre unité préférée pour le poids corporel",
    unitsHeightDescription: "Choisissez votre unité préférée pour la taille",
    unitsTemperatureDescription:
      "Choisissez votre unité préférée pour la température corporelle",
    unitsBloodGlucoseDescription:
      "Choisissez votre unité préférée pour la glycémie",
    unitsCholesterolDescription:
      "Choisissez votre unité préférée pour le cholestérol",
    unitsKilograms: "Kilogrammes (kg)",
    unitsPounds: "Livres (lb)",
    unitsStones: "Stones (st)",
    unitsCentimeters: "Centimètres (cm)",
    unitsInches: "Pouces (in)",
    unitsFeetInches: "Pieds et pouces (ft/in)",
    unitsCelsius: "Celsius (°C)",
    unitsFahrenheit: "Fahrenheit (°F)",
    unitsMostCountries: "La plupart des pays",
    unitsRegionUs: "États-Unis",
    unitsRegionUk: "Royaume-Uni",
    unitsRegionUsUk: "États-Unis, Royaume-Uni",
    unitsRegionUsJapan: "États-Unis, Japon",
    unitsSelectOption: "Sélectionner {unit}",
    unitsLoginRequired:
      "Veuillez vous connecter pour gérer vos préférences d'unités",
    unitsPageTitle: "Préférences d'unités",
    unitsPageDescription:
      "Personnalisez l'affichage des mesures dans HealthPass. Toutes les données sont stockées dans des unités médicales standard et converties automatiquement pour l'affichage.",
    unitsFollowLanguageTitle: "Les unités suivent la langue de l'application",
    unitsFollowLanguageDescription:
      "En anglais, HealthPass affiche les unités américaines (lb, ft/in, °F, mg/dL) ; dans les autres langues, les unités métriques. Vous pouvez choisir chaque unité ci-dessous.",
    unitsSaved: "Préférences enregistrées !",
    unitsResetTitle: "Suivre la langue de l'application",
    unitsResetDescription:
      "Abandonner vos choix et utiliser de nouveau les unités de la langue de l'application (anglais : unités américaines, autres langues : métriques).",
    unitsResetButton: "Réinitialiser",
    unitsLoading: "Chargement des préférences...",
    unitsInchesShort: "pouces",
    unitsFeetInchesShort: "pieds et pouces",
    unitsFeetPlaceholder: "Pieds",
    unitsInchesPlaceholder: "Pouces",
    unitsConverting: "Conversion...",
    unitsPreview: "Aperçu :",
    unitsYourPreferredUnit: "(votre unité préférée)",
  },
  es: {
    demoStepTravelTitle: "Planificar un viaje",
    demoStepTravelDescription:
      "Crear un viaje a Tailandia y obtener recomendaciones de vacunación",
    demoStepHealthDataTitle: "Datos de salud",
    demoStepHealthDataDescription: "Registrar medicamentos y vacunas",
    demoStepCheckinTitle: "Check-in",
    demoStepCheckinDescription:
      "Compartir datos de emergencia para un crucero o vuelo",
    demoStepEmergencyTitle: "Acceso de emergencia",
    demoStepEmergencyDescription:
      "Compartir datos con los primeros auxilios o un médico en el extranjero",
    demoStepCardTitle: "Tarjeta y seguro",
    demoStepCardDescription:
      "Reservar una cita y solicitar la repatriación sanitaria",
    demoExit: "Salir de la demo",
    demoStart: "Inicio",
    demoStepOf: "Paso {current} de {total}: {title}",
    demoFinish: "Finalizar",
    demoPageDescription:
      "Pruebe los 5 escenarios de demostración, desde la planificación del viaje hasta la atención de urgencia en el extranjero",
    demoCountriesBadge: "Más de 140 países",
    demoMultilingual: "Multilingüe",
    demoGdpr: "RGPD",
    demoTripTitle: "Viaje de demostración: {from} → {to}",
    demoTripDescription:
      "Acompañe a Terry Pazzo en su viaje a {country} y descubra el flujo completo de HealthPass, desde la planificación del viaje hasta la atención de urgencia.",
    demoStartButton: "Iniciar demo",
    demoScenariosHeading:
      "5 escenarios de demostración: empiece uno directamente:",
    demoScenarioLabel: "Escenario {n}",
    demoTestDataTitle: "Datos de prueba de la demo",
    demoDataUser: "Usuario",
    demoDataLogin: "Inicio de sesión",
    demoDataHomeCountry: "País de origen",
    demoDataDestination: "Destino",
    demoDataTravelPeriod: "Fechas del viaje",
    demoDataMedication: "Medicamento de demostración",
    demoDataVaccination: "Vacuna de demostración",
    demoDataVaccinationValue: "Hepatitis A",
    demoDataOperator: "Operador (check-in)",
    demoCompleteTitle: "¡Demo completada!",
    demoCompleteDescription:
      "Ha completado los 5 escenarios de demostración de HealthPass.",
    demoCompletedScenarios: "Escenarios completados",
    demoRestart: "Repetir la demo",
    demoToDashboard: "Ir al panel",
    profErrFirstName: "El nombre debe tener entre 2 y 50 caracteres.",
    profErrLastName: "El apellido debe tener entre 2 y 50 caracteres.",
    profErrPhone:
      "El número de teléfono debe tener entre 7 y 20 caracteres y solo puede contener dígitos, espacios, +, -, (, ).",
    profErrDateOfBirth:
      "Seleccione el día, el mes y el año de su fecha de nacimiento. La fecha debe estar entre 1900 y hoy.",
    profErrBirthDay: "Seleccione un día válido (1-31).",
    profErrBirthMonth: "Seleccione un mes válido.",
    profErrBirthYear: "Seleccione un año válido entre 1900 y hoy.",
    profErrNationality: "Seleccione una nacionalidad de la lista.",
    profErrAddress: "La dirección puede tener como máximo 200 caracteres.",
    profErrCity: "La ciudad puede tener como máximo 100 caracteres.",
    profErrCountry: "Seleccione un país de la lista.",
    profErrPostalCode:
      "El código postal puede tener como máximo 20 caracteres.",
    profErrCheckField: "Revise el campo «{field}»: {message}",
    profErrUnknownField: "Campo desconocido",
    profErrInvalidValue: "Valor no válido",
    profUpdateFailed: "Error al actualizar",
    profPhonePlaceholder: "+34 912 345 678",
    pwaInstallTitle: "Instalar HealthPass",
    pwaInstallDescription:
      "Instale HealthPass para un acceso más rápido y funciones sin conexión",
    pwaInstallButton: "Instalar",
    pwaLater: "Más tarde",
    pwaClose: "Cerrar",
    pwaUpdateTitle: "Actualización disponible",
    pwaUpdateDescription: "Hay una nueva versión de HealthPass disponible",
    pwaUpdateButton: "Actualizar",
    errbTitle: "Se ha producido un error técnico",
    errbMessage: "Vuelva a intentarlo o regrese a la página de inicio.",
    errbDeveloperDetails: "Detalles para desarrolladores",
    errbRetry: "Reintentar",
    errbHome: "Ir a la página de inicio",
    errbContactSupport:
      "Si el problema persiste, póngase en contacto con el soporte",
    settings2PasswordCurrentWrong: "La contraseña actual no es correcta.",
    settings2PasswordNotAllowed: "La nueva contraseña no está permitida.",
    settings2PasswordTooManyAttempts:
      "Demasiados intentos. Vuelva a intentarlo en 15 minutos.",
    settings2PasswordChangeFailed:
      "No se pudo cambiar su contraseña. Vuelva a intentarlo en breve.",
    settings2PasswordChanged:
      "Contraseña cambiada. Se ha cerrado su sesión en todos los dispositivos: vuelva a iniciar sesión con su nueva contraseña.",
    settings2AccountSecurity: "Seguridad de la cuenta",
    settings2CurrentPasswordPlaceholder: "Introduzca la contraseña actual",
    settings2NewPasswordPlaceholder: "Introduzca la nueva contraseña",
    settings2RepeatPasswordPlaceholder: "Repita la nueva contraseña",
    settings2PasswordsDoNotMatch: "Las nuevas contraseñas no coinciden.",
    settings2PortalLanguage: "Idioma del portal",
    settings2PortalLanguageHint: "Elija su idioma preferido para la interfaz",
    settings2Notifications: "Notificaciones",
    settings2GeneralNotifications: "Notificaciones generales",
    settings2EmailNotifications: "Notificaciones por correo electrónico",
    settings2SmsNotifications: "Notificaciones por SMS",
    settings2SmsNotificationsHint: "Notificaciones urgentes por SMS",
    settings2PushNotifications: "Notificaciones push",
    settings2PushUnsupported: "No compatible con este navegador",
    settings2PushBlocked:
      "Bloqueadas por el navegador: actívelas en la configuración del navegador",
    settings2PushActive: "Activas: notificaciones del navegador habilitadas",
    settings2PushEnableHint:
      "Activar avisos de consentimiento y salud en tiempo real",
    settings2PushUnavailableShort: "No disponible",
    settings2PushBlockedShort: "Bloqueado",
    settings2MedicationReminders: "Recordatorios de medicación",
    settings2MedicationRemindersHint: "Recordatorios para tomar su medicación",
    settings2TravelReminders: "Recordatorios de viaje",
    settings2TravelRemindersHint: "Vacunas y documentos para viajar",
    settings2HealthCheckReminders: "Recordatorios de chequeos médicos",
    settings2HealthCheckRemindersHint: "Chequeos preventivos periódicos",
    settings2DataManagement: "Gestión de datos",
    settings2ExportData: "Exportar datos",
    settings2StartExport: "Iniciar exportación",
    settings2MemberSince: "Miembro desde",
    settings2Trips: "Viajes",
    settings2PrivacySecurity: "Privacidad y seguridad",
    settings2PrivacySecurityText:
      "Sus datos se guardan cifrados y cumplen los requisitos del RGPD.",
    settings2StandardisedFormat: "Formato estandarizado:",
    settings2ConsentPageTitle: "Privacidad y consentimientos",
    settings2ConsentPageDescription:
      "Gestione sus preferencias de privacidad y sus consentimientos",
    settings2GdprPageTitle: "Cumplimiento del RGPD y exportación de datos",
    settings2GdprPageDescription:
      "Ejerza sus derechos sobre sus datos conforme al RGPD",
    settings2DeviceAndroid: "Dispositivo Android",
    settings2DeviceWindows: "PC con Windows",
    settings2DeviceThis: "Este dispositivo",
    settings2PasskeyAddFailed:
      "No se pudo añadir la llave de acceso. Vuelva a intentarlo.",
    settings2PasskeyRemoveFailed: "No se pudo eliminar la llave de acceso.",
    settings2EmailDeliveryFailed:
      "No pudimos enviar un código a su dirección, así que no se ha cambiado nada.",
    settings2EmailNoAddress:
      "Esta cuenta no tiene una dirección de correo electrónico a la que enviar un código.",
    settings2EmailSetupFailed:
      "No se pudo iniciar la configuración. Vuelva a intentarlo.",
    settings2CodeInvalid: "Ese código no es válido.",
    settings2SettingChangeFailed: "No se pudo cambiar ese ajuste.",
    settings2CheckingSecurity:
      "Comprobando la seguridad de su inicio de sesión...",
    settings2SecurityLoadFailed:
      "No se pudieron cargar sus ajustes de seguridad de inicio de sesión.",
    settings2TwoFactorTitle: "Autenticación de dos factores",
    settings2FactorPasskeyDescription:
      "Al iniciar sesión se pide su llave de acceso después de la contraseña.",
    settings2FactorEmailToDescription:
      "Al iniciar sesión se pide un código enviado a {email}.",
    settings2FactorEmailDescription:
      "Al iniciar sesión se pide un código enviado por correo electrónico.",
    settings2FactorNoneDescription:
      "Al iniciar sesión solo se pide su contraseña.",
    settings2BadgePasskey: "Llave de acceso",
    settings2BadgeEmailCode: "Código por correo",
    settings2BadgeNotEnabled: "No activada",
    settings2Passkeys: "Llaves de acceso",
    settings2WaitingForDevice: "Esperando al dispositivo...",
    settings2AddPasskey: "Añadir una llave de acceso",
    settings2PasskeysUnsupported:
      "Este navegador no admite llaves de acceso. La otra opción es un código por correo electrónico.",
    settings2ReauthMessage:
      "Por su seguridad, vuelva a iniciar sesión para cambiar sus métodos de inicio de sesión.",
    settings2SignInAgain: "Volver a iniciar sesión",
    settings2NoPasskeys:
      "Todavía no hay llaves de acceso. Añadir una sustituye el código por correo por la huella, el rostro o el PIN de su dispositivo.",
    settings2PasskeyAdded: "Añadida el {date}",
    settings2PasskeyLastUsed: "último uso el {date}",
    settings2PasskeyNotUsed: "aún no utilizada",
    settings2RemovePasskey: "Eliminar la llave de acceso {label}",
    settings2SecondPasskeyHint:
      "Merece la pena añadir una segunda llave de acceso por si alguna vez pierde este dispositivo.",
    settings2EmailCodeSuperseded:
      "En su lugar se usa su llave de acceso. El código por correo sería el más débil de los dos, por lo que no se ofrece mientras exista una llave de acceso.",
    settings2EmailCodeSentTo: "Al iniciar sesión se envía un código a {email}.",
    settings2EmailCodeSentToYours:
      "Al iniciar sesión se envía un código a su dirección.",
    settings2TurningOff: "Desactivando...",
    settings2TurnOff: "Desactivar",
    settings2CodeSentEnter:
      "Hemos enviado un código a {email}. Introdúzcalo para terminar de activarlo.",
    settings2YourAddress: "su dirección",
    settings2EmailedCode: "Código recibido por correo",
    settings2Checking: "Comprobando...",
    settings2Confirm: "Confirmar",
    settings2EmailCodeOffer:
      "Pedir un código por correo electrónico después de la contraseña.",
    settings2SendingCode: "Enviando un código...",
    settings2TurnOn: "Activar",
    miscToastLoginWelcome: "¡Inicio de sesión correcto! Bienvenido de nuevo.",
    miscToastPasswordResetSent:
      "Se ha enviado el correo para restablecer la contraseña.",
    miscToastDocumentUploaded: "Documento subido y cifrado correctamente.",
    miscToastDocumentsUploaded:
      "{count} documentos subidos y cifrados correctamente.",
    miscToastUploadFailed: "Error al subir el archivo. Vuelva a intentarlo.",
    miscToastUploadFailedWithReason: "Error al subir el archivo: {error}",
    miscToastDocumentDeleted: "Documento eliminado de forma segura.",
    miscToastDocumentDownloaded: "Documento descifrado y descargado.",
    miscToastHealthRecordSaved: "Datos de salud guardados correctamente.",
    miscToastVaccinationAdded: "Vacuna añadida correctamente.",
    miscToastMedicationAdded: "Medicamento añadido correctamente.",
    miscToastWellbeingSaved:
      "Datos de bienestar guardados en la base de datos FHIR.",
    miscToastWellbeingSaveFailed:
      "No se pudo guardar el bienestar. Vuelva a intentarlo.",
    miscToastSecurityAlert: "Alerta de seguridad: {message}",
    miscToastTinaReady: "¡Tina AI está lista para ayudar!",
    miscToastTinaUnavailable: "Tina AI no está disponible temporalmente.",
    miscToastUnknownError: "Se ha producido un error desconocido.",
    unitsChangedToast: "Unidad de {type} cambiada a {unit}",
    unitsUpdateFailed: "No se pudo actualizar la preferencia de unidad",
    unitsResetToast: "Las unidades vuelven a seguir el idioma de la aplicación",
    unitsResetFailed: "No se pudieron restablecer las preferencias de unidades",
    unitsWeightTitle: "Peso",
    unitsHeightTitle: "Estatura",
    unitsTemperatureTitle: "Temperatura",
    unitsBloodGlucoseTitle: "Glucosa en sangre",
    unitsCholesterolTitle: "Colesterol",
    unitsWeightDescription: "Elija su unidad preferida para el peso corporal",
    unitsHeightDescription: "Elija su unidad preferida para la estatura",
    unitsTemperatureDescription:
      "Elija su unidad preferida para la temperatura corporal",
    unitsBloodGlucoseDescription:
      "Elija su unidad preferida para la glucosa en sangre",
    unitsCholesterolDescription: "Elija su unidad preferida para el colesterol",
    unitsKilograms: "Kilogramos (kg)",
    unitsPounds: "Libras (lb)",
    unitsStones: "Stones (st)",
    unitsCentimeters: "Centímetros (cm)",
    unitsInches: "Pulgadas (in)",
    unitsFeetInches: "Pies y pulgadas (ft/in)",
    unitsCelsius: "Celsius (°C)",
    unitsFahrenheit: "Fahrenheit (°F)",
    unitsMostCountries: "La mayoría de los países",
    unitsRegionUs: "EE. UU.",
    unitsRegionUk: "Reino Unido",
    unitsRegionUsUk: "EE. UU., Reino Unido",
    unitsRegionUsJapan: "EE. UU., Japón",
    unitsSelectOption: "Seleccionar {unit}",
    unitsLoginRequired:
      "Inicie sesión para gestionar sus preferencias de unidades",
    unitsPageTitle: "Preferencias de unidades",
    unitsPageDescription:
      "Personalice cómo se muestran las mediciones en HealthPass. Todos los datos se guardan en unidades médicas estándar y se convierten automáticamente para mostrarlos.",
    unitsFollowLanguageTitle: "Las unidades siguen el idioma de la aplicación",
    unitsFollowLanguageDescription:
      "En inglés, HealthPass muestra unidades estadounidenses (lb, ft/in, °F, mg/dL); en los demás idiomas, unidades métricas. Puede elegir cada unidad a continuación.",
    unitsSaved: "¡Preferencias guardadas!",
    unitsResetTitle: "Seguir el idioma de la aplicación",
    unitsResetDescription:
      "Descartar sus elecciones y volver a usar las unidades del idioma de la aplicación (inglés: unidades estadounidenses; otros idiomas: métricas).",
    unitsResetButton: "Restablecer valores predeterminados",
    unitsLoading: "Cargando preferencias...",
    unitsInchesShort: "pulgadas",
    unitsFeetInchesShort: "pies y pulgadas",
    unitsFeetPlaceholder: "Pies",
    unitsInchesPlaceholder: "Pulgadas",
    unitsConverting: "Convirtiendo...",
    unitsPreview: "Vista previa:",
    unitsYourPreferredUnit: "(su unidad preferida)",
  },
  pt: {
    demoStepTravelTitle: "Planear uma viagem",
    demoStepTravelDescription:
      "Criar uma viagem à Tailândia e obter recomendações de vacinação",
    demoStepHealthDataTitle: "Dados de saúde",
    demoStepHealthDataDescription: "Registar medicamentos e vacinas",
    demoStepCheckinTitle: "Check-in",
    demoStepCheckinDescription:
      "Partilhar dados de emergência num cruzeiro ou voo",
    demoStepEmergencyTitle: "Acesso de emergência",
    demoStepEmergencyDescription:
      "Partilhar dados com socorristas ou um médico no estrangeiro",
    demoStepCardTitle: "Cartão e seguro",
    demoStepCardDescription:
      "Marcar uma consulta e pedir repatriamento sanitário",
    demoExit: "Sair da demonstração",
    demoStart: "Início",
    demoStepOf: "Passo {current} de {total}: {title}",
    demoFinish: "Concluir",
    demoPageDescription:
      "Experimente os 5 cenários de demonstração, do planeamento da viagem aos cuidados de emergência no estrangeiro",
    demoCountriesBadge: "Mais de 140 países",
    demoMultilingual: "Multilingue",
    demoGdpr: "RGPD",
    demoTripTitle: "Viagem de demonstração: {from} → {to}",
    demoTripDescription:
      "Acompanhe Terry Pazzo na sua viagem à {country} e conheça todo o fluxo do HealthPass, do planeamento da viagem aos cuidados de emergência.",
    demoStartButton: "Iniciar demonstração",
    demoScenariosHeading: "5 cenários de demonstração – comece um diretamente:",
    demoScenarioLabel: "Cenário {n}",
    demoTestDataTitle: "Dados de teste da demonstração",
    demoDataUser: "Utilizador",
    demoDataLogin: "Início de sessão",
    demoDataHomeCountry: "País de origem",
    demoDataDestination: "Destino",
    demoDataTravelPeriod: "Datas da viagem",
    demoDataMedication: "Medicamento de demonstração",
    demoDataVaccination: "Vacina de demonstração",
    demoDataVaccinationValue: "Hepatite A",
    demoDataOperator: "Operador (check-in)",
    demoCompleteTitle: "Demonstração concluída!",
    demoCompleteDescription:
      "Concluiu os 5 cenários de demonstração do HealthPass.",
    demoCompletedScenarios: "Cenários concluídos",
    demoRestart: "Repetir a demonstração",
    demoToDashboard: "Ir para o painel",
    profErrFirstName: "O nome próprio deve ter entre 2 e 50 caracteres.",
    profErrLastName: "O apelido deve ter entre 2 e 50 caracteres.",
    profErrPhone:
      "O número de telefone deve ter entre 7 e 20 caracteres e só pode conter algarismos, espaços, +, -, (, ).",
    profErrDateOfBirth:
      "Selecione o dia, o mês e o ano da sua data de nascimento. A data deve estar entre 1900 e hoje.",
    profErrBirthDay: "Selecione um dia válido (1-31).",
    profErrBirthMonth: "Selecione um mês válido.",
    profErrBirthYear: "Selecione um ano válido entre 1900 e hoje.",
    profErrNationality: "Selecione uma nacionalidade da lista.",
    profErrAddress: "A morada pode ter no máximo 200 caracteres.",
    profErrCity: "A localidade pode ter no máximo 100 caracteres.",
    profErrCountry: "Selecione um país da lista.",
    profErrPostalCode: "O código postal pode ter no máximo 20 caracteres.",
    profErrCheckField: "Verifique o campo «{field}»: {message}",
    profErrUnknownField: "Campo desconhecido",
    profErrInvalidValue: "Valor inválido",
    profUpdateFailed: "Falha na atualização",
    profPhonePlaceholder: "+351 912 345 678",
    pwaInstallTitle: "Instalar o HealthPass",
    pwaInstallDescription:
      "Instale o HealthPass para um acesso mais rápido e funcionalidades offline",
    pwaInstallButton: "Instalar",
    pwaLater: "Mais tarde",
    pwaClose: "Fechar",
    pwaUpdateTitle: "Atualização disponível",
    pwaUpdateDescription: "Está disponível uma nova versão do HealthPass",
    pwaUpdateButton: "Atualizar",
    errbTitle: "Ocorreu um erro técnico",
    errbMessage: "Tente novamente ou volte à página inicial.",
    errbDeveloperDetails: "Detalhes para programadores",
    errbRetry: "Tentar novamente",
    errbHome: "Ir para a página inicial",
    errbContactSupport: "Se o problema persistir, contacte o suporte",
    settings2PasswordCurrentWrong: "A palavra-passe atual não está correta.",
    settings2PasswordNotAllowed: "A nova palavra-passe não é permitida.",
    settings2PasswordTooManyAttempts:
      "Demasiadas tentativas. Tente novamente dentro de 15 minutos.",
    settings2PasswordChangeFailed:
      "Não foi possível alterar a sua palavra-passe. Tente novamente daqui a pouco.",
    settings2PasswordChanged:
      "Palavra-passe alterada. A sua sessão foi terminada em todo o lado – inicie sessão novamente com a nova palavra-passe.",
    settings2AccountSecurity: "Segurança da conta",
    settings2CurrentPasswordPlaceholder: "Introduza a palavra-passe atual",
    settings2NewPasswordPlaceholder: "Introduza a nova palavra-passe",
    settings2RepeatPasswordPlaceholder: "Repita a nova palavra-passe",
    settings2PasswordsDoNotMatch: "As novas palavras-passe não coincidem.",
    settings2PortalLanguage: "Idioma do portal",
    settings2PortalLanguageHint:
      "Escolha o seu idioma preferido para a interface",
    settings2Notifications: "Notificações",
    settings2GeneralNotifications: "Notificações gerais",
    settings2EmailNotifications: "Notificações por e-mail",
    settings2SmsNotifications: "Notificações por SMS",
    settings2SmsNotificationsHint: "Notificações urgentes por SMS",
    settings2PushNotifications: "Notificações push",
    settings2PushUnsupported: "Não suportado neste navegador",
    settings2PushBlocked:
      "Bloqueadas pelo navegador – ative-as nas definições do navegador",
    settings2PushActive: "Ativas – notificações do navegador ligadas",
    settings2PushEnableHint:
      "Ativar alertas de consentimento e saúde em tempo real",
    settings2PushUnavailableShort: "Indisponível",
    settings2PushBlockedShort: "Bloqueado",
    settings2MedicationReminders: "Lembretes de medicação",
    settings2MedicationRemindersHint: "Lembretes para tomar a sua medicação",
    settings2TravelReminders: "Lembretes de viagem",
    settings2TravelRemindersHint: "Vacinas e documentos para viajar",
    settings2HealthCheckReminders: "Lembretes de check-ups",
    settings2HealthCheckRemindersHint: "Check-ups preventivos regulares",
    settings2DataManagement: "Gestão de dados",
    settings2ExportData: "Exportar dados",
    settings2StartExport: "Iniciar exportação",
    settings2MemberSince: "Membro desde",
    settings2Trips: "Viagens",
    settings2PrivacySecurity: "Privacidade e segurança",
    settings2PrivacySecurityText:
      "Os seus dados são guardados encriptados e cumprem os requisitos do RGPD.",
    settings2StandardisedFormat: "Formato normalizado:",
    settings2ConsentPageTitle: "Privacidade e consentimentos",
    settings2ConsentPageDescription:
      "Gira as suas preferências de privacidade e os seus consentimentos",
    settings2GdprPageTitle: "Conformidade com o RGPD e exportação de dados",
    settings2GdprPageDescription:
      "Exerça os seus direitos sobre os seus dados ao abrigo do RGPD",
    settings2DeviceAndroid: "Dispositivo Android",
    settings2DeviceWindows: "PC Windows",
    settings2DeviceThis: "Este dispositivo",
    settings2PasskeyAddFailed:
      "Não foi possível adicionar a chave de acesso. Tente novamente.",
    settings2PasskeyRemoveFailed: "Não foi possível remover a chave de acesso.",
    settings2EmailDeliveryFailed:
      "Não conseguimos enviar um código para o seu endereço, por isso nada foi alterado.",
    settings2EmailNoAddress:
      "Esta conta não tem um endereço de e-mail para onde enviar um código.",
    settings2EmailSetupFailed:
      "Não foi possível iniciar a configuração. Tente novamente.",
    settings2CodeInvalid: "Esse código não é válido.",
    settings2SettingChangeFailed: "Não foi possível alterar essa definição.",
    settings2CheckingSecurity:
      "A verificar a segurança do seu início de sessão...",
    settings2SecurityLoadFailed:
      "Não foi possível carregar as suas definições de segurança de início de sessão.",
    settings2TwoFactorTitle: "Autenticação de dois fatores",
    settings2FactorPasskeyDescription:
      "O início de sessão pede a sua chave de acesso depois da palavra-passe.",
    settings2FactorEmailToDescription:
      "O início de sessão pede um código enviado para {email}.",
    settings2FactorEmailDescription:
      "O início de sessão pede um código enviado por e-mail.",
    settings2FactorNoneDescription:
      "O início de sessão pede apenas a sua palavra-passe.",
    settings2BadgePasskey: "Chave de acesso",
    settings2BadgeEmailCode: "Código por e-mail",
    settings2BadgeNotEnabled: "Não ativada",
    settings2Passkeys: "Chaves de acesso",
    settings2WaitingForDevice: "A aguardar o dispositivo...",
    settings2AddPasskey: "Adicionar uma chave de acesso",
    settings2PasskeysUnsupported:
      "Este navegador não suporta chaves de acesso. A alternativa é um código por e-mail.",
    settings2ReauthMessage:
      "Por segurança, inicie sessão novamente para alterar os seus métodos de início de sessão.",
    settings2SignInAgain: "Iniciar sessão novamente",
    settings2NoPasskeys:
      "Ainda não há chaves de acesso. Adicionar uma substitui o código por e-mail pela impressão digital, rosto ou PIN do seu dispositivo.",
    settings2PasskeyAdded: "Adicionada a {date}",
    settings2PasskeyLastUsed: "última utilização a {date}",
    settings2PasskeyNotUsed: "ainda não utilizada",
    settings2RemovePasskey: "Remover a chave de acesso {label}",
    settings2SecondPasskeyHint:
      "Vale a pena adicionar uma segunda chave de acesso caso perca este dispositivo.",
    settings2EmailCodeSuperseded:
      "É usada a sua chave de acesso. O código por e-mail seria o mais fraco dos dois, por isso não é oferecido enquanto existir uma chave de acesso.",
    settings2EmailCodeSentTo:
      "Ao iniciar sessão é enviado um código para {email}.",
    settings2EmailCodeSentToYours:
      "Ao iniciar sessão é enviado um código para o seu endereço.",
    settings2TurningOff: "A desativar...",
    settings2TurnOff: "Desativar",
    settings2CodeSentEnter:
      "Enviámos um código para {email}. Introduza-o para concluir a ativação.",
    settings2YourAddress: "o seu endereço",
    settings2EmailedCode: "Código recebido por e-mail",
    settings2Checking: "A verificar...",
    settings2Confirm: "Confirmar",
    settings2EmailCodeOffer:
      "Pedir um código por e-mail depois da palavra-passe.",
    settings2SendingCode: "A enviar um código...",
    settings2TurnOn: "Ativar",
    miscToastLoginWelcome: "Sessão iniciada com sucesso! Bem-vindo de volta.",
    miscToastPasswordResetSent:
      "Foi enviado o e-mail para redefinir a palavra-passe.",
    miscToastDocumentUploaded: "Documento carregado e encriptado com sucesso.",
    miscToastDocumentsUploaded:
      "{count} documentos carregados e encriptados com sucesso.",
    miscToastUploadFailed: "Falha no carregamento. Tente novamente.",
    miscToastUploadFailedWithReason: "Falha no carregamento: {error}",
    miscToastDocumentDeleted: "Documento eliminado de forma segura.",
    miscToastDocumentDownloaded: "Documento desencriptado e transferido.",
    miscToastHealthRecordSaved: "Dados de saúde guardados com sucesso.",
    miscToastVaccinationAdded: "Vacina adicionada com sucesso.",
    miscToastMedicationAdded: "Medicamento adicionado com sucesso.",
    miscToastWellbeingSaved:
      "Dados de bem-estar guardados na base de dados FHIR.",
    miscToastWellbeingSaveFailed:
      "Não foi possível guardar o bem-estar. Tente novamente.",
    miscToastSecurityAlert: "Alerta de segurança: {message}",
    miscToastTinaReady: "A Tina AI está pronta para ajudar!",
    miscToastTinaUnavailable: "A Tina AI está temporariamente indisponível.",
    miscToastUnknownError: "Ocorreu um erro desconhecido.",
    unitsChangedToast: "Unidade de {type} alterada para {unit}",
    unitsUpdateFailed: "Não foi possível atualizar a preferência de unidade",
    unitsResetToast: "As unidades voltam a seguir o idioma da aplicação",
    unitsResetFailed: "Não foi possível repor as preferências de unidades",
    unitsWeightTitle: "Peso",
    unitsHeightTitle: "Altura",
    unitsTemperatureTitle: "Temperatura",
    unitsBloodGlucoseTitle: "Glicemia",
    unitsCholesterolTitle: "Colesterol",
    unitsWeightDescription:
      "Escolha a sua unidade preferida para o peso corporal",
    unitsHeightDescription: "Escolha a sua unidade preferida para a altura",
    unitsTemperatureDescription:
      "Escolha a sua unidade preferida para a temperatura corporal",
    unitsBloodGlucoseDescription:
      "Escolha a sua unidade preferida para a glicemia",
    unitsCholesterolDescription:
      "Escolha a sua unidade preferida para o colesterol",
    unitsKilograms: "Quilogramas (kg)",
    unitsPounds: "Libras (lb)",
    unitsStones: "Stones (st)",
    unitsCentimeters: "Centímetros (cm)",
    unitsInches: "Polegadas (in)",
    unitsFeetInches: "Pés e polegadas (ft/in)",
    unitsCelsius: "Celsius (°C)",
    unitsFahrenheit: "Fahrenheit (°F)",
    unitsMostCountries: "A maioria dos países",
    unitsRegionUs: "EUA",
    unitsRegionUk: "Reino Unido",
    unitsRegionUsUk: "EUA, Reino Unido",
    unitsRegionUsJapan: "EUA, Japão",
    unitsSelectOption: "Selecionar {unit}",
    unitsLoginRequired:
      "Inicie sessão para gerir as suas preferências de unidades",
    unitsPageTitle: "Preferências de unidades",
    unitsPageDescription:
      "Personalize a forma como as medições são apresentadas no HealthPass. Todos os dados são guardados em unidades médicas padrão e convertidos automaticamente para apresentação.",
    unitsFollowLanguageTitle: "As unidades seguem o idioma da aplicação",
    unitsFollowLanguageDescription:
      "Em inglês, o HealthPass apresenta unidades dos EUA (lb, ft/in, °F, mg/dL); nos outros idiomas, unidades métricas. Pode escolher cada unidade abaixo.",
    unitsSaved: "Preferências guardadas!",
    unitsResetTitle: "Seguir o idioma da aplicação",
    unitsResetDescription:
      "Descartar as suas escolhas e voltar a usar as unidades do idioma da aplicação (inglês: unidades dos EUA; outros idiomas: métricas).",
    unitsResetButton: "Repor predefinições",
    unitsLoading: "A carregar preferências...",
    unitsInchesShort: "polegadas",
    unitsFeetInchesShort: "pés e polegadas",
    unitsFeetPlaceholder: "Pés",
    unitsInchesPlaceholder: "Polegadas",
    unitsConverting: "A converter...",
    unitsPreview: "Pré-visualização:",
    unitsYourPreferredUnit: "(a sua unidade preferida)",
  },
};

export type AppPagesStrings = CoreStrings &
  (typeof appPagesAuthStrings)["en"] &
  (typeof appPagesLandingStrings)["en"];

function merged(language: Language): AppPagesStrings {
  return {
    ...core[language],
    ...appPagesAuthStrings[language],
    ...appPagesLandingStrings[language],
  };
}

export const appPagesStrings: Record<Language, AppPagesStrings> = {
  en: merged("en"),
  de: merged("de"),
  fr: merged("fr"),
  es: merged("es"),
  pt: merged("pt"),
};
